import { Table, TableBody, TableHead, TableRow, Tooltip } from '@mui/material';
import {
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  ActionIconButton,
  NoDataSkeleton,
  StatusChip,
  TableBodySkeleton,
} from '../../../common/components';
import {
  INVENTORY_ARTICLE_STATUS_CONFIG,
  INVENTORY_ARTICLES_TABLE_TEXTS as TEXTS,
} from '../constants/inventoryArticlesConstants';
import { getArticleUnitLabel } from '../utils/inventoryArticlesUtils';

const textSx = { textAlign: 'left' };

const COLUMNS = [
  { key: 'code', label: TEXTS.code, sx: { ...textSx, width: 140 } },
  { key: 'name', label: TEXTS.name, sx: textSx },
  { key: 'description', label: TEXTS.description, sx: { ...textSx, width: 260 } },
  { key: 'unit', label: TEXTS.unit, sx: { ...textSx, width: 130 } },
  { key: 'externalCode', label: TEXTS.externalCode, sx: { ...textSx, width: 160 } },
  { key: 'status', label: TEXTS.status, sx: { width: 120 } },
  { key: 'actions', label: TEXTS.actions, sx: { width: 140 } },
];

/**
 * Listado de artículos de inventario con estado y acciones de detalle, edición y baja.
 * @param {Object} props
 * @param {Array<Object>} props.data Artículos de la página.
 * @param {Array<Object>} props.units Unidades para resolver la descripción.
 * @param {boolean} props.isLoading Carga en curso.
 * @param {boolean} props.actionsDisabled Bloquea las acciones mientras hay una operación en curso.
 * @param {Function} props.onEdit Abre la edición.
 * @param {Function} props.onDelete Pide confirmar la baja.
 * @param {Function} props.onView Abre el detalle.
 * @returns {JSX.Element}
 */
const InventoryArticlesDataTable = ({
  data,
  units = [],
  isLoading,
  actionsDisabled = false,
  onEdit,
  onDelete,
  onView,
}) => {
  return (
    <GeneralTableBox fixedLayout minTableWidth={1100}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {COLUMNS.map((column) => (
              <HeadTableCell key={column.key} sx={column.sx}>
                {column.label}
              </HeadTableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {isLoading ? (
            <TableBodySkeleton />
          ) : data && data.length > 0 ? (
            data.map((article) => {
              const status =
                INVENTORY_ARTICLE_STATUS_CONFIG[article.is_active ? 'active' : 'inactive'];
              const description = article.description || '-';
              const unitLabel = getArticleUnitLabel(article, units);
              const externalCode = article.external_code || '-';
              return (
                <TableRow key={article.uuid} hover>
                  <RowTableCell sx={{ ...textSx, fontWeight: 700 }} title={article.code}>
                    {article.code}
                  </RowTableCell>
                  <RowTableCell sx={textSx} title={article.name}>
                    {article.name}
                  </RowTableCell>
                  <RowTableCell sx={textSx} title={description}>
                    {description}
                  </RowTableCell>
                  <RowTableCell sx={textSx} title={unitLabel}>
                    {unitLabel}
                  </RowTableCell>
                  <RowTableCell sx={textSx} title={externalCode}>
                    {externalCode}
                  </RowTableCell>
                  <RowTableCell>
                    <StatusChip size="small" color={status.color} label={status.label} />
                  </RowTableCell>
                  <RowTableCell>
                    <Tooltip title={TEXTS.view}>
                      <span>
                        <ActionIconButton
                          variant="view"
                          onClick={() => onView?.(article)}
                          disabled={actionsDisabled}
                        />
                      </span>
                    </Tooltip>
                    <Tooltip title={TEXTS.edit}>
                      <span>
                        <ActionIconButton
                          variant="edit"
                          onClick={() => onEdit(article)}
                          disabled={actionsDisabled}
                        />
                      </span>
                    </Tooltip>
                    <Tooltip title={TEXTS.delete}>
                      <span>
                        <ActionIconButton
                          variant="delete"
                          onClick={() => onDelete(article)}
                          disabled={actionsDisabled}
                        />
                      </span>
                    </Tooltip>
                  </RowTableCell>
                </TableRow>
              );
            })
          ) : (
            <NoDataSkeleton message={TEXTS.empty} colSpan={COLUMNS.length} />
          )}
        </TableBody>
      </Table>
    </GeneralTableBox>
  );
};

export default InventoryArticlesDataTable;
